import { useCallback, useEffect, useState } from "react";
import type { User as OidcUser } from "oidc-client-ts";
import {
  devModeEnabled,
  devUser,
  getUserManager,
  loginRedirect,
  logoutRedirect,
  userFromOidc,
  type FlowUser,
} from "@/lib/auth";
import { authStore } from "@/lib/authStore";

interface UseAuthValue {
  user: FlowUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (returnTo?: string | null) => Promise<void>;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthValue {
  const [user, setUser] = useState<FlowUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    if (devModeEnabled()) {
      const u = devUser();
      authStore.set(true);
      if (mounted) {
        setUser(u);
        setIsLoading(false);
      }
      return () => {
        mounted = false;
      };
    }

    const mgr = getUserManager();
    void mgr
      .getUser()
      .then((u) => {
        if (!mounted) return;
        const flow = userFromOidc(u);
        authStore.set(Boolean(flow));
        setUser(flow);
      })
      .catch(() => {
        if (!mounted) return;
        authStore.set(false);
        setUser(null);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    const handleUserLoaded = (u: OidcUser) => {
      const flow = userFromOidc(u);
      authStore.set(Boolean(flow));
      setUser(flow);
    };
    const handleUserUnloaded = () => {
      authStore.set(false);
      setUser(null);
    };
    mgr.events.addUserLoaded(handleUserLoaded);
    mgr.events.addUserUnloaded(handleUserUnloaded);
    return () => {
      mounted = false;
      mgr.events.removeUserLoaded(handleUserLoaded);
      mgr.events.removeUserUnloaded(handleUserUnloaded);
    };
  }, []);

  // 🔴 The parameter is LOAD-BEARING and was silently dropped. This read
  // `useCallback(() => loginRedirect(), [])` — zero args — so `login(returnTo)` in LoginPage
  // threw its argument away, `loginRedirect` defaulted the target to "/", and Keycloak
  // round-tripped `state: { returnTo: "/" }`. Every deep link taken by someone who was not
  // already logged in — a notification email, a Ctrl+K hit, a shared ticket — landed on Home.
  // TypeScript CANNOT catch this: a zero-arg function is a valid subtype of a one-arg one, so
  // the declared `(returnTo?: string | null) => Promise<void>` accepted an implementation that
  // ignores it. Only the cold e2e path can (tests/e2e/live/deeplink-cold.spec.ts).
  const login = useCallback((returnTo?: string | null) => loginRedirect(returnTo), []);
  const logout = useCallback(() => logoutRedirect(), []);

  return {
    user,
    isAuthenticated: Boolean(user),
    isLoading,
    login,
    logout,
  };
}
